import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

/**
 * GeoLocationService provides browser geolocation functionality with permission handling.
 * Uses BehaviorSubjects for reactive state management.
 */
@Injectable({
  providedIn: 'root'
})
export class GeoLocationService {
  private permissionStatus = new BehaviorSubject<'granted' | 'denied' | 'prompt' | null>(null);
  private coordinates = new BehaviorSubject<{ lat: number | null; lon: number | null }>({ lat: null, lon: null });
  private isLoading = new BehaviorSubject<boolean>(false);
  private error = new BehaviorSubject<string | null>(null);

  constructor() {
    this.checkPermission();
  }

  /**
   * Checks the geolocation permission status via navigator.permissions API.
   * Sets up a listener for permission state changes.
   */
  async checkPermission(): Promise<void> {
    if ('permissions' in navigator) {
      try {
        const permission = await navigator.permissions.query({ name: 'geolocation' });
        this.permissionStatus.next(permission.state as 'granted' | 'denied' | 'prompt');
        permission.onchange = () => this.checkPermission();
      } catch (error) {
        console.error('Error checking geolocation permission:', error);
        this.permissionStatus.next('prompt');
      }
    } else {
      console.warn('Permissions API not supported');
      this.permissionStatus.next('prompt');
    }
  }

  /**
   * Requests the user's current location with a 10-second timeout.
   * Updates coordinates on success, or error state on failure.
   */
  requestLocation(): void {
    this.isLoading.next(true);
    this.error.next(null);

    if (!navigator.geolocation) {
      this.isLoading.next(false);
      this.permissionStatus.next('denied');
      this.error.next('Geolocation is not supported by your browser.');
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        this.coordinates.next({
          lat: position.coords.latitude,
          lon: position.coords.longitude
        });
        this.permissionStatus.next('granted');
        this.isLoading.next(false);
      },
      (error) => {
        console.error('Error getting location:', error);
        this.permissionStatus.next('denied');
        this.isLoading.next(false);

        this.error.next(
          error.code === error.PERMISSION_DENIED
            ? 'Location access denied. Please allow it in your browser settings.'
            : 'Failed to get your location. Please try again or enter it manually.'
        );
      },
      { timeout: 10000 }
    );
  }

  /**
   * Allows external components to set coordinates manually.
   * Used by map picker and manual input fields.
   */
  setCoordinates(lat: number | null, lon: number | null): void {
    this.coordinates.next({ lat, lon });
  }

  /**
   * Observable for permission status changes.
   */
  getPermissionStatus(): Observable<'granted' | 'denied' | 'prompt' | null> {
    return this.permissionStatus.asObservable();
  }

  /**
   * Observable for coordinate changes.
   */
  getCoordinates(): Observable<{ lat: number | null; lon: number | null }> {
    return this.coordinates.asObservable();
  }

  /**
   * Observable for loading state.
   */
  getIsLoading(): Observable<boolean> {
    return this.isLoading.asObservable();
  }

  /**
   * Observable for error messages.
   */
  getError(): Observable<string | null> {
    return this.error.asObservable();
  }
}
